import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import momentTZ from 'moment-timezone';
import { Field, reduxForm, formValueSelector, SubmissionError } from 'redux-form';
import {
  Button, FormGroup, ControlLabel, FormControl,
  HelpBlock, Collapse, Tabs, Tab, Modal,
  Tooltip, OverlayTrigger, Dropdown, MenuItem,
} from 'react-bootstrap';
import { Link, Prompt } from 'react-router-dom';
import draftToHtml from 'draftjs-to-html';
import { CompositeDecorator, EditorState, Modifier, convertToRaw, ContentState, /* convertFromHTML */ } from 'draft-js';
import {
  reduce, filter, orderBy, concat, trim, each, indexOf, find, endsWith, keyBy, trimEnd,
  first, tail, pick, delay, cloneDeep, without, sortBy,
} from 'lodash';
import Fa from 'react-fontawesome';
import Toggle from 'react-bootstrap-toggle';
import Joi from 'joi';

import Editor from '../../../../components/editor';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import {
  fetchAutomationEmail, updateAutomationEmail,
  createAutomationEmail, fetchDiscoveryBotLogs,
} from '../../SetupActions';
import { getFeedbackRequestEmailSettings } from '../../SetupReducer';
import { getMeta } from '../../FilterReducer';
import ProgressBar from '../../../../components/progress-bar';
import RecipientAutocomplete from './RecipientAutocomplete';
import SearchForm from '../../../../components/search-form';
import { getSearchQuery, cleanSubmitCriteria, getIsDirty } from '../../../../components/search-form/search-form-reducer';
import { checkDirty } from '../../../../components/search-form/search-form-actions';
import TagControl from './TagControl';
import TokenPanel from './TokenPanel';
import { getFooterHeight } from '../../../App/AppReducer';
import { getFilteredTokens, getTranslatedTokens } from './tokens';
import OpportunityOwnerField from './OpportunityOwnerField';
import OpportunityStatusField from './OpportunityStatusField';
import ReminderLookbackField from './fields/ReminderLookbackField';
import DateField from './fields/DateField'; // backward compatible
import CopyQueryControl from './CopyQueryControl';
import ModifiedLog from './ModifiedLog';
import SearchPathModal from './SearchPathModal';
import ConfirmButton from '../../../../components/confirm-button';
import PathDisplay from '../../../../components/search-path/path';
import ImageUpload from '../../../Admin/components/NewsForm/ImageUpload';
import { contactListHandleStrategy, ContactListToken, ContactTokenModal } from '../../components/ContactListDecorator';

import styles from './styles.css';

let htmlToDraft = () => null;

const translatedTokens = getTranslatedTokens();




const TIMEZONES = sortBy(keyBy(reduce(momentTZ.tz.countries(), (items, country) => {
  const countryTimeZones = momentTZ.tz.zonesForCountry(country, true);
  each(countryTimeZones, (countryTimeZone) => {
    items.push({
      label: countryTimeZone.name,
      value: countryTimeZone.name,
      offset: countryTimeZone.offset,
    });
  });
  return items;
}, []), 'value'), 'value');

function checkPaths(meta, type, paths, syncRequired = false) {
  const pickAttr = (node) => {
    return pick(node, ['name', 'description']);
  };
  const parser = (startingObject, subPaths) => {
    if (!subPaths || subPaths.length === 0) {
      return false;
    }
    const head = first(subPaths);
    const field = find(startingObject.fields, { name:  head });
    if (!field) {
      return null;
    }
    if (syncRequired) {
      if (!field.sync_required && field.is_system) {
        return null;
      }
    }
    const isRef = field.type === 'reference';
    if (isRef) {
      const toObject = first(field.reference_to);
      if (toObject) {
        const obj = find(meta, { name: toObject });
        if (obj) {
          const remaining = tail(subPaths);
          const names = [pickAttr(startingObject)];
          if (remaining.length === 0) {
            return [...names, pickAttr(obj)];
          }
          const pNames = parser(obj, remaining);
          if (pNames) {
            return [...names, ...pNames];
          }
          return null;
        }
      }
      return null;
    }
    return [pickAttr(startingObject), pickAttr(field)]; // a normal field
  };

  const head = first(paths);
  if (!head) {
    return null;
  }
  const startingObject = find(meta, { name: head, type }) || find(meta, { localName: head, type });
  if (!startingObject) {
    return null;
  }
  const remaining = tail(paths);
  if (remaining.length === 0) {
    return null;
  }
  return parser(startingObject, remaining);
};

function getMatches(tokenStrings, metaObjects, input, startingObjectType = 'Opportunity', includeCustomFields = false) {
  const tokenRegex = new RegExp(`${tokenStrings.join('|')}`, 'g');
  const customR = `{(\\w)+(${TokenPanel.SEPARATOR})+((\\w)+(${TokenPanel.SEPARATOR})*)*}`;
  const customRegex = new RegExp(customR, 'g');
  const allRegex = includeCustomFields ? customRegex : new RegExp(`${tokenStrings.join('|')}`, 'g');

  const results = [];
  let matchArr = allRegex.exec(input);

  while (matchArr !== null) {
    const [token] = matchArr;
    const rawToken = trim(token, '{}');
    const paths = rawToken.split(TokenPanel.SEPARATOR);
    const isCustom = includeCustomFields && token.indexOf(TokenPanel.SEPARATOR) > 0;

    let labels = null;
    let valid = false;
    if (isCustom) {
      labels = includeCustomFields ? checkPaths(metaObjects, startingObjectType, paths, true) : null;
      valid = labels !== null;
    } else {
      valid = tokenRegex.exec(input) !== null;
    }
    results.push({
      token,
      start: matchArr.index,
      end: matchArr.index + token.length,
      paths,
      labels,
      valid,
    });
    matchArr = allRegex.exec(input);
  }
  return results;
}

function makeTokenStrategy(filteredTokens, metaObjects, startingObjectType = 'Opportunity', includeCustomFields = false) {
  const allTokens = reduce(filteredTokens, (items, tokenGroup) => {
    each(tokenGroup.values, (token) => {
      items.push(trim(token.value, '{}'));
    });
    return items;
  }, []);
  // const tokenRegex = new RegExp(`(\\{{1})(${allTokens.join('|')})(\\}{1})`, 'g');
  const tokenStrings = reduce(allTokens, (itms, tkn) => {
    if (endsWith(tkn, 'URL')) {
      itms.push(`{${tkn}}(.*?){/${tkn}}`);
    }
    itms.push(`{${tkn}}`);
    return itms;
  }, []);

  // tokenStrings.push(`{(.*?)}`);
  // const tokenRegex = new RegExp(`${tokenStrings.join('|')}`, 'g');

  return (contentBlock, callback) => {
    /*
    const text = contentBlock.getText();
    let matchArr = tokenRegex.exec(text);
    let start = 0;
    while (matchArr !== null) {
      start = matchArr.index;
      const end = start + matchArr[0].length;
      callback(start, end);
      matchArr = tokenRegex.exec(text);
    }
    */
    const input = contentBlock.getText();
    const results = getMatches(tokenStrings, metaObjects, input, startingObjectType, includeCustomFields);
    each(results, (match) => {
      if (match.valid) {
        callback(match.start, match.end);
      }
    });
  };
}

const TokenSpan = (props) => {
  const { decoratedText, offsetkey, children } = props;
  let inner = children;

  let k = decoratedText;
  const te = (new RegExp('{(.*?)}', 'g')).exec(k);
  if (te && te.length) {
    [k] = te;
  }

  if (translatedTokens[k]) {
    const tip = (
      <Tooltip id={(offsetkey || decoratedText)}>
        <div>{translatedTokens[k]}</div>
      </Tooltip>
    );
    inner = (
      <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={tip}>
        <span>{children}</span>
      </OverlayTrigger>
    );
  }

  return (
    <span data-text data-offset-key={offsetkey} className={styles.segmented}>
      {inner}
    </span>
  );
};
TokenSpan.propTypes = {
  offsetkey: PropTypes.string,
  children: PropTypes.node,
  decoratedText: PropTypes.string.isRequired,
};
TokenSpan.defaultProps = {
  offsetkey: null,
  children: null,
};

function getSearchFormName(type) {
  return `automations/${type}-email`;
}

class FeedbackRequestEmailSetting extends Component {
  constructor(props) {
    super(props);

    const { source } = props.match?.params;
    const automationSource = source === 'accounts' ? 'account' : 'opportunity';
    const isAccount = automationSource === 'account';

    const filteredTokens = getFilteredTokens(props.type, automationSource);

    this.state = {
      loading: false,
      loaded: false,
      editorState: null,
      recipients: [],
      focus: false,
      selectedTab: null,
      searchQuery: null,
      formReady: false,
      showSubjectTokenModal: false,
      showBodyTokenModal: false,
      defaultSendFrom: null,
      tokens: filteredTokens,
      formUpdate: 0,
      sticky: false,
      showSearchPathSelector: false,
      settings: {},
      isAccount,
      automationSource,
      logs: [],
      selectedLog: null,
      openContactTokenModal: false,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.onToggleEnable = this.onToggleEnable.bind(this);
    this.renderField = this.renderField.bind(this);
    this.handleRecipientChange = this.handleRecipientChange.bind(this);
    this.onEditorStateChange = this.onEditorStateChange.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.renderEditor = this.renderEditor.bind(this);
    this.handleTabSelect = this.handleTabSelect.bind(this);

    this.addTokenToSubject = this.addTokenToSubject.bind(this);
    this.addTokenToBody = this.addTokenToBody.bind(this);

    this.closeSubjectTokenModal = this.closeSubjectTokenModal.bind(this);
    this.closeBodyTokenModal = this.closeBodyTokenModal.bind(this);
    this.handleWeekdayClick = this.handleWeekdayClick.bind(this);
    this.handleCloneQuery = this.handleCloneQuery.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.updateSearchPath = this.updateSearchPath.bind(this);
    this.removeSearchPath = this.removeSearchPath.bind(this);
    this.showSearchPathSelector = this.showSearchPathSelector.bind(this);
    this.hideSearchPathSelector = this.hideSearchPathSelector.bind(this);
    this.handleSelectedLogChange = this.handleSelectedLogChange.bind(this);
    this.renderWeekdayField = this.renderWeekdayField.bind(this);
    this.handleContactTokenChange = this.handleContactTokenChange.bind(this);
    this.handleContactTokenClick = this.handleContactTokenClick.bind(this);
    this.decorators = [
      ...(props.type === 'summary' ? [
        {
          strategy: contactListHandleStrategy,
          component: ContactListToken,
          props: {
            onClick: this.handleContactTokenClick,
          },
        },
      ] : []),
      // it's better to have 2 decorators, conversion will be done in 2 pharses
      {
        strategy: makeTokenStrategy(filteredTokens, props.meta, isAccount ? 'Account' : 'Opportunity', false),
        component: TokenSpan,
      },
      {
        strategy: makeTokenStrategy(filteredTokens, props.meta, isAccount ? 'Account' : 'Opportunity', true),
        component: TokenSpan,
      },
    ];
  }

  componentDidMount() {
    this.mounted = true;
    this.fetchData();
    import('html-to-draftjs').then((f)=> {
      htmlToDraft = f.default;
    });
  }

  componentWillUnmount() {
    this.mounted = false;
    this.destroyStickyFooter();
  }

  onToggleEnable() {
    const { enabled } = this.props;
    this.props.change('enabled', !enabled);
  }

  onEditorStateChange(editorState) {
    this.setState({
      editorState,
    }, () => {
      const string = draftToHtml(convertToRaw(editorState.getCurrentContent()));
      this.props.change('email_body', string);
    });
  }

  handleContactTokenChange(contactToken, stateReplacer) {
    const nextEditorState = stateReplacer(this.state.editorState);
    this.onEditorStateChange(nextEditorState);
  }

  handleContactTokenClick() {
    this.setState({ openContactTokenModal: true });
  }

  handleSelectedLogChange(log) {
    const settings = log?.settings || this.state.settings;
    if (this.props.isReminder) {
      settings.lookback_period = `${settings.sync_lookback_period} ${settings.sync_lookback_unit}`;
      settings.start_date = moment(settings.start_date); // backward compatible, viewing history logs
    }
    this.props.initialize(settings);
    this.setEmailBody(settings.email_body || '');
    this.setRecipients(settings.$send_to_cc || []);
    delay(() => {
      this.setState({
        selectedLog: log,
        searchQuery: settings.search_query || {},
        defaultSendFrom: settings.default_send_from,
        formUpdate: this.state.formUpdate + 1
      });
    }, 500);
  }

  handleWeekdayClick(name, val) {
    const { disabled, weekdays } = this.props;
    if (!disabled) {
      let newValues = [...weekdays];
      if (indexOf(newValues, val) >= 0) {
        newValues = without(newValues, val);
      } else {
        newValues.push(val);
      }
      this.props.change(name, newValues);
    }
  }

  fetchLogs() {
    return new Promise((logsResolve, logsReject) => {
      this.props.fetchLogs()
        .then((logs) => {
          const sortedLogs = logs?.sort((a, b) => new Date(b.date) - new Date(a.date)) || [];
          this.setState({ logs: sortedLogs });
          logsResolve(sortedLogs);
        }, (error) => {
          logsReject(error);
        },
      );
    });
  }

  setEmailBody(string) {
    const compositeDecorator = new CompositeDecorator(this.decorators);
    const s = string === '<p></p>\n' ? '<p>.</p>' : string;

    /*
    const contentBlock = convertFromHTML(s);
    const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks, contentBlock.entityMap);
    const editorState = EditorState.createWithContent(contentState, compositeDecorator);
    */

    const contentBlock = htmlToDraft(s);
    const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks, contentBlock.entityMap);
    const editorState = EditorState.createWithContent(contentState, compositeDecorator);

    this.setState({ editorState });
  }

  setRecipients(recipients) {
    const users = orderBy(filter(recipients, r => !r.isEmail), ['first_name', 'last_name'], ['asc', 'asc']);
    const emails = orderBy(filter(recipients, r => r.isEmail), ['email'], ['asc']);
    this.setState({ recipients: concat(users, emails) });
  }

  initStickyFooter() {
    if (this.footerRef && this.sentinalRef) {
      this.observer = new window.IntersectionObserver((records) => {
        console.log('--');
        if (!records[0].isIntersecting) {
          this.setState({ sticky: true });
        } else {
          this.setState({ sticky: false });
        }
      });
      this.observer.observe(this.sentinalRef);
    }
  }

  destroyStickyFooter() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  closeSubjectTokenModal() {
    this.setState({ showSubjectTokenModal: false });
  }

  closeBodyTokenModal() {
    this.setState({ showBodyTokenModal: false });
  }

  addTokenToSubject(token) {
    const { emailSubject } = this.props;
    const subject = emailSubject || '';

    let start = (emailSubject || '').length;
    if (this.input) {
      start = this.input.selectionStart;
    }

    const output = `${subject.substring(0, start)} ${token.value} ${subject.substring(start)}`;
    this.props.change('email_subject', trim(output));
    this.closeSubjectTokenModal();
  }

  addTokenToBody(token) {
    const text = token.editorValue || token.value;
    const { editorState } = this.state;
    const selectionState = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const modifiedContent = Modifier.insertText(contentState, selectionState, text);
    const nextState = EditorState.push(editorState, modifiedContent, editorState.getLastChangeType());
    this.setState({ editorState: nextState });
    this.closeBodyTokenModal();
  }

  handleFocus() {
    this.setState({ focus: true });
  }

  handleBlur() {
    this.setState({ focus: false });
  }

  handleCloneQuery(searchQuery) {
    this.setState({ searchQuery, formReady: false, formUpdate: this.state.formUpdate + 1 }, () => {
      this.props.change('_search_query', this.state.formUpdate + 1);
      this.props.checkDirty();
    });
  }

  handleSearchChange() {
    this.setState({ formReady: true });
    this.props.checkDirty();
  }

  handleRecipientChange(recipients) {
    const cc = reduce(recipients, (items, recipient) => {
      if (recipient.isEmail) {
        items.push(recipient.email);
      } else {
        items.push(recipient._id);
      }
      return items;
    }, []);
    this.props.change('send_to_cc', cc);
    this.setRecipients(recipients);
  }

  updateSearchPath(paths) {
    const { sendToDynamic } = this.props;
    const currentIndexed = keyBy(sendToDynamic, (k) => {
      return k.join('.');
    });
    const newPath = paths.join('.');
    if (!currentIndexed[newPath]) {
      const newParams = [...sendToDynamic, paths];
      this.props.change('send_to_dynamic', newParams);
    }
    this.hideSearchPathSelector();
  }

  removeSearchPath(paths) {
    const { sendToDynamic } = this.props;
    const newPath = paths.join('.');
    const newParams = reduce(sendToDynamic, (items, item) => {
      const k = item.join('.');
      if (k !== newPath) {
        items.push(item);
      }
      return items;
    }, []);
    this.props.change('send_to_dynamic', newParams);
  }

  showSearchPathSelector() {
    this.setState({ showSearchPathSelector: true });
  }

  hideSearchPathSelector() {
    this.setState({ showSearchPathSelector: false });
  }

  handleSubmit(data) {
    const params = { ...data };
    params.number_of_emails = Number.parseInt(params.number_of_emails, 10);
    params.days_between_emails = Number.parseInt(params.days_between_emails, 10);
    delete params.$send_to_cc;
    params.session_id = this.props.sessionId;

    if (this.state.formReady) {
      const searchQuery = cleanSubmitCriteria(this.props.searchQuery);
      params.search_query = searchQuery;
    } else {
      params.search_query = cloneDeep(this.state.settings.search_query);
    }

    if (params.lookback_period) {
      const pieces = params.lookback_period.split(' ');
      const [syncLookbackPeriod, syncLookbackUnit] = pieces;
      params.sync_lookback_period = syncLookbackPeriod;
      params.sync_lookback_unit = syncLookbackUnit;
    }

    return this.props.updateData(params)
      .then(
        (settings) => {
          if (this.props.isReminder) {
            settings.lookback_period = `${settings.sync_lookback_period} ${settings.sync_lookback_unit}`;
          }
          this.setEmailBody(settings.email_body);
          this.setRecipients(settings.$send_to_cc || []);
          const el = (<div>Settings updated successfully</div>);
          this.context.addNotification(el, { type: 'success', autoClose: true });

          const { id } = this.props.match.params;
          const isNew = id === 'new';
          if (isNew) {
            const uri = trimEnd(this.props.location.pathname, '/new');
            this.context.router.history.replace(`${uri}/${settings._id}`);
          }
          this.setState({ settings, formUpdate: this.state.formUpdate + 1, searchQuery: settings.search_query || {}, defaultSendFrom: settings.default_send_from }, () => {
            this.props.initialize(settings);
            this.props.checkDirty();
            this.fetchLogs();
          });
        },
        (errors) => {
          const e = errors.data || errors;
          const message = e.message || 'Unknown errors - Code: 114';
          const el = (<div>{message}</div>);
          this.context.addNotification(el, { type: 'error', autoClose: true });
          this.checkError(e);
          throw new SubmissionError(e);
        },
      );
  }

  handleTabSelect(selectedTab) {
    this.setState({ selectedTab });
  }

  fetchData() {
    this.setState({ loading: true, loaded: false });
    this.props.fetchData()
      .then(
        (settings) => {
          if (this.mounted) {
            if (this.props.isReminder) {
              settings.lookback_period = `${settings.sync_lookback_period} ${settings.sync_lookback_unit}`;
            }
            this.props.initialize(settings);
            this.setRecipients(settings.$send_to_cc || []);
            this.setEmailBody(settings.email_body || '');
            this.setState({
              loading: false,
              loaded: true,
              searchQuery: settings.search_query || {},
              defaultSendFrom: settings.default_send_from,
              settings,
            }, () => {
              this.initStickyFooter();
              this.fetchLogs();
            });
          }
        },
        () => {
          if (this.mounted) {
            // redirect?
            this.setState({ loading: false, loaded: false });
          }
        },
      );
  }

  checkError(e) {
    const tabs = [
      {
        tab: 'toAndFrom',
        fields: ['send_from', 'send_to_owner', 'send_to_cc'],
        found: false,
      },
      {
        tab: 'emailContent',
        fields: ['email_subject', 'email_body'],
        found: false,
      },
      {
        tab: 'search',
        fields: ['search_query'],
        found: false,
      },
      {
        tab: 'frequency',
        fields: ['number_of_emails', 'days_between_emails'],
        found: false,
      },
    ];
    each(e, (v, k) => {
      each(tabs, (tab) => {
        if (indexOf(tab.fields, k) >= 0) {
          tab.found = true;
        }
      });
    });
    const firstTab = find(tabs, { found: true });
    if (firstTab) {
      this.setState({ selectedTab: firstTab.tab });
    }
  }

  getTitle() {
    const {
      isReminder, isSummary, isAlert,
      isExclusion,
    } = this.props;
    let title = 'Email Settings';
    if (isSummary) {
      title = 'Summary of Feedback Requests';
    } else if (isReminder) {
      title = 'Feedback Request Reminders';
    } else if (isAlert) {
      title = 'Feedback Request Termination Alerts';
    } else if (isExclusion) {
      title = 'Exclusion Alerts';
    }
    return title;
  }

  renderTitle() {
    const { type } = this.props;
    const title = this.getTitle();
    return (
      <Link to={`/setup/feedback-requests/${type}-emails`}><Fa name="arrow-left" /> {title}</Link>
    );
  }

  renderEditor({
    label, editorState, focus, meta: { touched, error },
  }) {
    const validationState = touched && error ? 'error' : null;
    const { isAccount } = this.state;
    const { isDeleted } = this.props;

    return (
      <FormGroup validationState={validationState} className={styles.descriptionEditor}>
        <ControlLabel>{label}</ControlLabel>
        <Editor
          editorState={editorState}
          onEditorStateChange={this.onEditorStateChange}
          wrapperClassName={styles.editorWrapper}
          editorClassName={`${styles.editor} ${focus ? styles.editorFocus : ''}`}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          toolbar={{
            options: [
              'inline', 'blockType', 'fontSize',
              'fontFamily', 'list', 'textAlign', 'colorPicker',
              'link', 'emoji', 'remove', 'history',
            ],
            inline: {
              options: ['bold', 'italic', 'underline', 'strikethrough', 'superscript', 'subscript'],
            },
          }}
          toolbarCustomButtons={[
            ...(isDeleted ? [] : [<TagControl
              key="tag-1"
              tokens={this.state.tokens}
              source={isAccount ? 'account' : 'opportunity'}
              onClick={() => {
                this.setState({ showBodyTokenModal: true });
              }}
            />]),
            <ImageUpload
              key="image"
              config={{
                uploadCallback: this.uploadImageCallBack,
                previewImage: true,
                urlEnabled: true,
                uploadEnabled: false,
                inputAccept: 'image/gif,image/jpeg,image/jpg,image/png,image/svg,video/webm',
                alt: { present: false, mandatory: false },
                defaultSize: {
                  height: 'auto',
                  width: 'auto',
                },
              }}
            />,
          ]}
          customDecorators={this.decorators}
          readOnly={this.state.selectedLog !== null || isDeleted}
        />
        {touched && error && <HelpBlock>{error}</HelpBlock>}
      </FormGroup>
    );
  }

  renderField({
    input, label, type, meta: { touched, error }, componentClass, placeholder, options,
    inputGroupBtn, inputRef, disabled,
  }) {
    const { submitting, isDeleted } = this.props;
    const { selectedLog } = this.state;
    const validationState = touched && error ? 'error' : null;
    let control = null;
    let formFeedback = (<FormControl.Feedback />);
    if (componentClass !== 'select') {
      control = (<FormControl {...input} inputRef={inputRef} componentClass={componentClass} placeholder={(placeholder || label)} type={type} disabled={submitting || selectedLog !== null || isDeleted} />);
      if (inputGroupBtn) {
        control = (
          <div className="input-group">
            {control}
            {inputGroupBtn}
          </div>
        );
        formFeedback = null; // weird style
      }
    } else if (componentClass === 'select') {
      control = (
        <FormControl {...input} componentClass={componentClass} placeholder={(placeholder || label)} type={type} disabled={(submitting || disabled || selectedLog !== null || isDeleted)}>
          {options !== null && options !== undefined && options.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}
        </FormControl>
      );
    }

    return (
      <FormGroup validationState={validationState}>
        {label !== null && label !== undefined && label.length > 0 && <ControlLabel>{label}</ControlLabel>}
        {control}
        {formFeedback}
        {touched && error && <HelpBlock>{error}</HelpBlock>}
      </FormGroup>
    );
  }

  renderWeekdayField({
    input, meta: { touched, error }, type, label,
  }) {
    const { disabled, weekdays } = this.props;

    const validationState = (touched || type === 'hidden') && error ? 'error' : null;

    const stl = { cursor: disabled ? 'not-allowed' : 'pointer', marginBottom: '5px', marginLeft: '10px' };

    const statuses = [
      { label: 'Monday', value: 1 },
      { label: 'Tuesday', value: 2 },
      { label: 'Wednesday', value: 3 },
      { label: 'Thursday', value: 4 },
      { label: 'Friday', value: 5 },
      { label: 'Saturday', value: 6 },
      { label: 'Sunday', value: 7 },
    ];

    return (
      <FormGroup validationState={validationState}>
        {label !== null && label !== undefined && label.length > 0 && <ControlLabel>{label}</ControlLabel>}
        {statuses.map((status) => {
          const isActive = indexOf(weekdays, status.value) >= 0;
          const cls = isActive ? '' : 'text-muted';
          const icon = isActive ? 'check-square-o' : 'square-o';
          const iconCls = isActive ? 'text-active' : '';
          return (
            <div
              style={stl}
              key={status.value}
              onClick={() => {
                this.handleWeekdayClick(input.name, status.value);
              }}
            >
              <Fa className={iconCls} name={icon} /> <span className={cls}>{status.label}</span>
            </div>
          );
        })}
        {(touched || type === 'hidden') && error && <HelpBlock>{error}</HelpBlock>}
      </FormGroup>
    );
  }

  renderCollectorSelector() {
    const {
      validCollectors, selectedTVCollectorId,
      isCollectorRequired,
    } = this.props;

    if (!isCollectorRequired) {
      return null;
    }

    const allCollectors = reduce(validCollectors, (items, item) => {
      items.push({
        value: item.collector_id,
        label: item.is_active ? item.collector_name : `${item.collector_name} (inactive)`,
      });
      return items;
    }, [{
      value: null,
      label: 'Select TruVoice survey collector',
    }]);

    const selectedCollector = selectedTVCollectorId ? find(validCollectors, { collector_id: selectedTVCollectorId }) : null;

    return (
      <div>
        <div>
          {isCollectorRequired &&
            <Field
              label="Survey Collector"
              name="tv_collector_id"
              componentClass="select"
              options={allCollectors}
              component={this.renderField}
            />
          }
          {isCollectorRequired && selectedCollector && !selectedCollector.is_active &&
            <div className="text-danger">
              <Fa name="exclamation-triangle" /> Collector <strong>{selectedCollector.collector_name}</strong> is inactive.
            </div>
          }
        </div>
        <p>&nbsp;</p>
      </div>
    );
  }

  renderToggle() {
    const {
      isSummary, submitting, enabled,
      isReminder, isAlert, isExclusion, isDeleted,
    } = this.props;
    const { selectedLog } = this.state;

    let label = 'Email';

    let help = null;
    if (isSummary) {
      label = 'Summary Email';
      help = enabled ? 'Summary emails for opportunities with at least one feedback request will be sent based on the settings below.'
        : 'Summary emails are disabled.';
    } else if (isReminder) {
      label = 'Reminder Email';
      help = enabled ? 'Email reminders to submit feedback requests for closed opportunities will be sent based on the settings below.'
        : 'Email reminders to submit feedback requests are disabled.';
    } else if (isAlert) {
      label = 'Alert Email';
      help = enabled ? 'Trusales will send an email alert whenever a sales rep terminates outreach for one or more contacts'
        : 'Termination Alert Emails are disabled.';
    } else if (isExclusion) {
      label = 'Alert Email';
      help = enabled ? 'Trusales will send an email alert whenever a sales rep excludes an opportunity for feedback request'
        : 'Opportunity Exclusion Alert Emails are disabled.';
    }
    return (
      <FormGroup>
        <ControlLabel>{label}</ControlLabel>
        {' '}
        <Toggle
          onClick={this.onToggleEnable}
          on={<span>Enabled</span>}
          off={<span>Disabled</span>}
          onstyle="success"
          size="xs"
          offstyle="danger"
          active={enabled}
          disabled={submitting || selectedLog !== null || isDeleted}
        />
        <HelpBlock>{help}</HelpBlock>
      </FormGroup>
    );
  }

  renderFrequency() {
    const {
      isSummary, hasWeekdaySettings,
    } = this.props;
    if (isSummary) {
      return null;
    }
    return (
      <div>
        <Field
          label="How many email reminders"
          name="number_of_emails"
          component={this.renderField}
        />
        <Field
          label="Days between reminders"
          name="days_between_emails"
          component={this.renderField}
        />

        {hasWeekdaySettings &&
          <>
            <Field
              label="Reminders will be sent on"
              name="weekdays"
              component={this.renderWeekdayField}
            />

            <Field
              label="Timezone"
              name="timezone"
              componentClass="select"
              options={TIMEZONES}
              component={this.renderField}
            />

          </>
        }
      </div>
    );
  }

  renderDynamicRecipientList() {
    const { submitting, sendToDynamic, isDeleted } = this.props;
    const { showSearchPathSelector, isAccount, selectedLog } = this.state;

    const rows = sendToDynamic.map((fields) => {
      const k = fields.join('.');
      return (
        <tr key={k}>
          <td>
            <PathDisplay startingNode={isAccount ? 'Account' : 'Opportunity'} paths={fields} />
          </td>
          <td width="80" className="text-right">
            <ConfirmButton
              disabled={submitting || selectedLog !== null || isDeleted}
              confirmMessage="Sure?"
              confirmStyle="danger"
              size="xs"
              onConfirm={() => {
                this.removeSearchPath(fields);
              }}
            >
              <span><Fa name="trash" /> Remove</span>
            </ConfirmButton>
          </td>
        </tr>
      );
    });

    return (
      <FormGroup>
        <ControlLabel>Dynamic Recipients:</ControlLabel>
        {sendToDynamic.length > 0 &&
          <table style={{ margin: 0 }} className="table table-striped table-condensed table-bordered">
            <tbody>
              {rows}
            </tbody>
          </table>
        }
        <SearchPathModal
          onClose={this.hideSearchPathSelector}
          onAdd={this.updateSearchPath}
          open={showSearchPathSelector}
          isAccount={isAccount}
        />
        {selectedLog === null && !isDeleted &&
          <div>
            <Button
              onClick={this.showSearchPathSelector}
              bsStyle="link"
              disabled={submitting}
            >
              <Fa name="plus" /> Add Field
            </Button>
          </div>
        }
      </FormGroup>
    );
  }

  renderEmailSenderAndRecipients() {
    const {
      sendToOwner, submitting, organization, sendFrom,
      ownerMustBeEnabled, isDeleted,
    } = this.props;
    const { defaultSendFrom, isAccount, selectedLog } = this.state;

    const lbl = isAccount ? 'account/company' : 'opportunity/deal';

    const subLabel = ownerMustBeEnabled ? '(Owner must be enabled and connected to TruSales)' : '';
    const options = [
      { value: true, label: `Send email to ${lbl} owner ${subLabel}` },
      { value: false, label: `Do not send email to ${lbl} owner` },
    ];

    return (
      <div>
        <Field
          label="Send email from"
          name="send_from"
          component={this.renderField}
        />
        {(!sendFrom || trim(sendFrom) === '') && trim(defaultSendFrom) !== '' &&
          <HelpBlock><em>* If left empty, emails will be sent from TruSales&rsquo; system email <code>{defaultSendFrom}</code></em></HelpBlock>
        }
        <p />
        <FormGroup>
          <ControlLabel>Send to {lbl} owner:</ControlLabel>
          <div style={{ marginLeft: 10 }}>
            {options.map((option) => {
              const active = sendToOwner === option.value;
              return (
                <div
                  key={option.value}
                  style={{ cursor: selectedLog !== null || isDeleted ? 'not-allowed' : 'pointer' }}
                  onClick={() => { if (selectedLog === null && !isDeleted) this.props.change('send_to_owner', option.value); }}
                >
                  <Fa name={active ? 'check-circle' : 'circle-o'} className={active ? 'text-success' : ''} /> {option.label}
                </div>
              );
            })}
          </div>
        </FormGroup>
        <FormGroup>
          <ControlLabel>Recipient List:</ControlLabel>
          <RecipientAutocomplete
            organization={organization}
            recipients={this.state.recipients}
            submitting={submitting || selectedLog !== null || isDeleted}
            onChange={this.handleRecipientChange}
          />
        </FormGroup>
        {this.renderDynamicRecipientList()}
      </div>
    );
  }

  renderSubjectTokenSelector() {
    const { showSubjectTokenModal, selectedLog } = this.state;
    const { submitting, isDeleted } = this.props;
    return (
      <span className="input-group-btn">
        <Button
          disabled={submitting || selectedLog != null || isDeleted}
          active={showSubjectTokenModal}
          bsStyle="default"
          onClick={() => { this.setState({ showSubjectTokenModal: true }); }}
        >
          <code style={{ background: 'none', color: '#222' }}>{'{token}'}</code>
        </Button>
      </span>
    );
  }

  renderEmailContent() {
    const { focus, editorState } = this.state;
    const { emailLogo, isDeleted } = this.props;
    return (
      <div>
        <Field
          label="Email Subject"
          name="email_subject"
          component={this.renderField}
          inputGroupBtn={this.renderSubjectTokenSelector()}
          inputRef={(ref) => { this.input = ref; }}
        />
        <Field
          label="Email Body"
          name="email_body"
          focus={focus}
          editorState={editorState}
          component={this.renderEditor}
        />
        <FormGroup>
          <ControlLabel
            style={{ cursor: (this.state.selectedLog !== null || isDeleted) ? 'not-allowed' : 'pointer' }}
            onClick={() => { if (this.state.selectedLog === null && !isDeleted) this.props.change('email_logo', !emailLogo) }}
          >
            <Fa className={emailLogo ? 'text-active' : ''} name={emailLogo ? 'check-square' : 'square-o'} /> Show TruSales Logo
          </ControlLabel>
        </FormGroup>
      </div>
    );
  }

  renderCopyQueryControl() {
    const { submitting } = this.props;
    const { searchQuery, selectedLog, isAccount } = this.state;
    let hasQuery = false;
    if (searchQuery && searchQuery.values && searchQuery.values.length > 0) {
      hasQuery = true;
    }
    return (
      <div className={hasQuery ? 'pull-right' : 'pull-left'}>
        <div className="clearfix">
          {!hasQuery && <small style={{ display: 'inline-block', paddingTop: '7px' }} className="text-muted">&nbsp;OR&nbsp;</small>}
          <CopyQueryControl
            disabled={submitting || selectedLog !== null}
            shouldConfirm={hasQuery}
            isAccount={isAccount}
            onDone={this.handleCloneQuery}
          />
        </div>
      </div>
    );
  }

  renderUnsaveWarningMessage(isDirty) {
    const { submitting } = this.props;
    const { formReady, selectedLog } = this.state;
    if (submitting || !formReady || !isDirty || selectedLog !== null) {
      return null;
    }
    return (
      <span className="text-danger animated flash" style={{ paddingTop: 5 }}>
        <Fa name="exclamation-triangle" /> You have unsaved data. &nbsp;
        <Prompt message="You have unsaved changes, are you sure you want to leave?" />
      </span>
    );
  }



  renderSearchForm() {
    const {
      submitting, type, isReminder, isSummary,
      isAlert, ownerMustBeEnabled, syncLookback,
      opportunityStatus, isDeleted,
    } = this.props;
    const {
      searchQuery,
      formUpdate,
      automationSource,
      isAccount,
      selectedLog,
    } = this.state;
    let eType = null;
    if (isReminder) {
      eType = 'reminder';
    } else if (isSummary) {
      eType = 'summary';
    } else if (isAlert) {
      eType = 'termination alert';
    }

    const viewingLog = selectedLog !== null;

    const innerFields = [];
    if (isReminder) {
      if (viewingLog && !selectedLog.settings?.sync_lookback_period && !selectedLog.settings?.sync_lookback_unit) {
        innerFields.push((
          <div key="date-field" style={{ marginBottom: '10px' }}>
            <DateField
              value={selectedLog.settings?.start_date}
              path={[]}
              level={1}
              showDelete={false}
              showInclude={false}
              disabled={submitting || selectedLog !== null || isDeleted}
              isAccount={isAccount}
              searchCriteria={{
                _id: 'start-date',
                type: 'include',
                filter: {
                  _id: 'start-date-filter',
                  label: 'Start Date',
                },
              }}
              onChange={(date) => { this.props.change('start_date', date ? date.startOf('day').toDate() : null); }}
            />
          </div>
        ));
      } else {
        innerFields.push((
          <div key="date-field" style={{ marginBottom: '10px' }}>
            <ReminderLookbackField
              value={syncLookback}
              path={[]}
              level={1}
              showDelete={false}
              showInclude={false}
              disabled={submitting || selectedLog !== null || isDeleted}
              isAccount={isAccount}
              searchCriteria={{
                _id: 'lookback-period',
                type: 'include',
                filter: {
                  _id: 'lookback-period',
                  label: 'Lookback Period',
                },
              }}
            />
          </div>
        ));
      }
    }
    innerFields.push((
      <div key="owner-field" style={{ marginBottom: '10px' }}>
        <OpportunityOwnerField
          value={ownerMustBeEnabled}
          path={[]}
          level={1}
          showDelete={false}
          showInclude={false}
          isAccount={isAccount}
          disabled={submitting || selectedLog !== null || isDeleted}
          searchCriteria={{
            _id: 'owner-enabled',
            type: 'include',
            filter: {
              _id: 'owner-enabled-filter',
              label: isAccount ? 'Account Owner' : 'Opportunity Owner',
            },
          }}
          onChange={(value) => { this.props.change('owner_must_be_enabled', value); }}
        />
      </div>
    ));

    if (!isAccount) {
      innerFields.push((
        <div key={`${automationSource}-owner`} className="animated fadeIn" >
          <OpportunityStatusField
            value={opportunityStatus}
            path={['opportunity_status']}
            level={1}
            showDelete={false}
            showInclude={false}
            disabled={submitting || selectedLog !== null || isDeleted}
            searchCriteria={{
              _id: 'opportunity-status',
              type: 'include',
              filter: {
                _id: 'opportunity-status',
                label: 'Opportunity Status',
              },
            }}
            onChange={this.props.change}
            searchType={type}
            onUpdate={() => {}}
          />
        </div>
      ));
    }

    return (
      <div>
        <ControlLabel>
          Only opportunities that match the filter(s) below will be eligible for {eType} emails:
        </ControlLabel>
        <SearchForm
          key={`form-${formUpdate}`}
          dropup
          onSearchChange={this.handleSearchChange}
          disabled={submitting || selectedLog !== null || isDeleted}
          standalone
          showRootLogicalORWarning
          simpleMode
          defaultCriteria={searchQuery || {
            type: 'groupAND',
            values: [],
            mappings: [],
          }}
          type={getSearchFormName(type)}
          customFooterControls={this.renderCopyQueryControl()}
        >
          {innerFields}
        </SearchForm>
      </div>
    );
  }

  renderModifiedLogDropdown() {
    const { logs, settings, selectedLog } = this.state;
    if (settings._id === undefined || settings._id === null) {
      // displaying the new form (adding new settings and not editing existent settings)
      return null;
    }
    return (
      <Dropdown id="modified-log-dropdown" dropup>
        <Dropdown.Toggle bsStyle="link" bsSize="sm">
          <span className="text-active">
            <ModifiedLog value={selectedLog !== null ? selectedLog.settings : settings} historical={selectedLog !== null} />
            {' '}
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu style={{ maxHeight: 300, overflowY: 'auto' }}>
          {logs.map((log, index) => {
              const historical = index !== 0; // hmm?
              const items = [
                ...(historical ? [] : [(<MenuItem header key="header">Current Settings:</MenuItem>)]),
                (
                  <MenuItem
                    key={log._id}
                    eventKey={log._id}
                    active={this.state.selectedLog?._id === log._id}
                    onClick={() => this.handleSelectedLogChange(historical ? log : null)}
                  >
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<ModifiedLog value={log.settings} muted={false} historical={historical} />
                </MenuItem>
                ),
                ...(historical ? [] : [(<MenuItem divider key="div" />), (<MenuItem header key="header2">Historical Settings:</MenuItem>)]),
              ];
              return items;
            })}
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  renderContent() {
    const {
      handleSubmit, submitting, valid,
      isReminder,
      footerHeight, dirty, formDirty,
      type, isDeleted,
    } = this.props;
    const {
      selectedTab,
      formReady,
      settings,
      isAccount,
      selectedLog,
      logs,
    } = this.state;
    const isDirty = dirty || formDirty;
    const viewingLog = selectedLog !== null;

    return (
      <form onSubmit={handleSubmit(this.handleSubmit)}>
        <div className={`panel panel-default ${styles.panel}`}>
          <div className="panel-heading">
            {this.renderTitle()}
          </div>

          <div className={`panel-body ${styles.panelBody} ${(this.state.sticky ? styles.sticky : '')}`}>

            <div className="alert alert-warning">
              {isAccount &&
                <div>
                  {this.getTitle()} for Accounts/Companies
                </div>
              }
              {!isAccount &&
                <div>
                  {this.getTitle()} for Opportunities/Deals
                </div>
              }
            </div>
            {selectedLog !== null && <div className="alert alert-danger">
              <Fa name="exclamation-triangle" /> You are currently viewing a historical log. The form is in read-only mode, and no changes can be made.
            </div>}
            {isDeleted && <div className="alert alert-danger">
              <Fa name="exclamation-triangle" /> This automaton was deleted. The form is in read-only mode, and no changes can be made.
            </div>}
            <Field
              label={`${type} email group name`}
              name="name"
              disabled={viewingLog}
              component={this.renderField}
            />
            <p>&nbsp;</p>

            {this.renderToggle()}
            <p>&nbsp;</p>

            {this.renderCollectorSelector()}

            <Collapse in>
              <div>
                <Tabs
                  activeKey={(selectedTab || 'toAndFrom')}
                  onSelect={this.handleTabSelect}
                  id="email-form-tabs"
                  animation={false}
                >
                  <Tab
                    eventKey="toAndFrom"
                    title="Sender and Recipients"
                  >
                    <div>
                      <p />
                      {this.renderEmailSenderAndRecipients()}
                    </div>
                  </Tab>
                  <Tab
                    eventKey="emailContent"
                    title="Email Content"
                  >
                    <div>
                      <p />
                      {this.renderEmailContent()}
                    </div>
                  </Tab>
                  <Tab
                    eventKey="search"
                    title="Trigger Conditions"
                  >
                    <div>
                      <p />
                      {this.renderSearchForm()}
                    </div>
                  </Tab>
                  {isReminder &&
                    <Tab
                      eventKey="frequency"
                      title="Email Frequency"
                    >
                      <div>
                        <p />
                        {this.renderFrequency()}
                      </div>
                    </Tab>
                  }
                </Tabs>
              </div>
            </Collapse>
          </div>
          <div
            ref={(el) => { this.footerRef = el; }}
            className={`panel-footer ${styles.panelFooter} ${(this.state.sticky ? styles.sticky : '')}`}
            style={{ bottom: `${footerHeight}px` }}
          >
            <div className="clearfix">
              <div className="pull-left" style={{ padding: '5px 10px' }}>
                {logs?.length > 1
                  ? this.renderModifiedLogDropdown()
                  : <ModifiedLog value={settings} />
                }
              </div>
              {(selectedLog === null && !isDeleted) &&
                <div className="pull-right text-right">
                  {this.renderUnsaveWarningMessage(isDirty)}
                  <Button className={(isDirty && formReady) ? 'animated flash' : ''} bsStyle="primary" disabled={!valid || submitting} type="submit">
                    {!submitting && <span>Save</span>}
                    {submitting && <span><Fa name="spinner" spin /> Saving</span>}
                  </Button>
                </div>
              }
            </div>
          </div>
          <div ref={(el) => { this.sentinalRef = el; }} />
        </div>
        <div style={{ height: '100px' }} />
      </form>
    );
  }

  renderTokenModal() {
    if (this.props.isDeleted) {
      return null;
    }
    const { showSubjectTokenModal, isAccount, showBodyTokenModal } = this.state;
    return (
      <Modal show={(showSubjectTokenModal || showBodyTokenModal)} onHide={showBodyTokenModal ? this.closeBodyTokenModal : this.closeSubjectTokenModal}>
        <Modal.Header closeButton>
          <Modal.Title>Email Tokens</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TokenPanel
            addToken={showBodyTokenModal ? this.addTokenToBody : this.addTokenToSubject}
            tokens={this.state.tokens}
            source={isAccount ? 'account' : 'opportunity'}
            restrict={!showBodyTokenModal}
            showCustomFields
          />
        </Modal.Body>
        <Modal.Footer>
          <Button bsStyle="link" onClick={showBodyTokenModal ? this.closeBodyTokenModal : this.closeSubjectTokenModal}>Cancel</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  renderContactTokenModal() {
    const { openContactTokenModal } = this.state;
    return (
      <ContactTokenModal
        open={openContactTokenModal}
        onClose={() => {
          this.setState({ openContactTokenModal: false });
        }}
        onChange={this.handleContactTokenChange}
      />
    );
  }

  render() {
    const { loading, loaded } = this.state;
    const isLoading = loading || !loaded;
    return (
      <div>
        {isLoading && <ProgressBar />}
        {!isLoading && this.renderContent()}
        {!isLoading && this.renderTokenModal()}
        {!isLoading && this.renderContactTokenModal()}
      </div>
    );
  }
}

const selector = formValueSelector('feedbackRequestEmailSettings'); // <-- same as form name

// Retrieve data from store as props
function mapStateToProps(state, ownProps) {
  const isReminder = ownProps.type === 'reminder';
  const isSummary = ownProps.type === 'summary';
  const isAlert = ownProps.type === 'alert';
  const isExclusion = ownProps.type === 'exclusion';

  // const dateRaw = selector(state, 'start_date');
  // const startDate = dateRaw ? moment(dateRaw, 'YYYY-MM-DD') : null;


  const syncLookbackPeriod = selector(state, 'sync_lookback_period');
  const syncLookbackUnit = selector(state, 'sync_lookback_unit');
  const syncLookback = `${syncLookbackPeriod} ${syncLookbackUnit}`;

  return {
    isReminder,
    isSummary,
    isAlert,
    isExclusion,
    organization: getUserCurrentOrganization(state),
    settings: getFeedbackRequestEmailSettings(state, ownProps.type),
    enabled: selector(state, 'enabled'),
    sendToOwner: selector(state, 'send_to_owner'),
    emailSubject: selector(state, 'email_subject'),
    sendFrom: selector(state, 'send_from'),
    searchQuery: getSearchQuery(getSearchFormName(ownProps.type), state),
    sendToDynamic: selector(state, 'send_to_dynamic'),
    emailLogo: selector(state, 'email_logo'),
    ownerMustBeEnabled: selector(state, 'owner_must_be_enabled'),
    // startDate,
    formDirty: getIsDirty(getSearchFormName(ownProps.type), state),
    footerHeight: getFooterHeight(state),
    selectedTVCollectorId: selector(state, 'tv_collector_id'),
    isCollectorRequired: selector(state, 'isCollectorRequired'),
    opportunityStatus: selector(state, 'opportunity_status'),
    automationSource: selector(state, 'source'),
    syncLookbackPeriod,
    syncLookbackUnit,
    syncLookback,
    isDeleted: selector(state, 'is_deleted'),
    weekdays: selector(state, 'weekdays'),
    hasWeekdaySettings: selector(state, 'timezone') !== undefined && selector(state, 'weekdays') !== undefined,
    meta: getMeta(state),
  };
}

function mapDispatchToProps(dispatch, ownProps) {
  const { id, source } = ownProps.match.params;
  return {
    // dispatching plain actions
    fetchData: () => dispatch(fetchAutomationEmail(ownProps.type, source, id === 'new' ? 'NEW' : id)),
    // fetchLogs: () => dispatch(fetchDiscoveryBotLogs({ id, minimumDate: moment().subtract(3, 'month').format() })),
    fetchLogs: () => dispatch(fetchDiscoveryBotLogs({ id })),
    updateData: (params) => {
      if (id === 'new') {
        return dispatch(createAutomationEmail(ownProps.type, params));
      }
      return dispatch(updateAutomationEmail(ownProps.type, id, params))
    },
    checkDirty: () => dispatch(checkDirty(getSearchFormName(ownProps.type))),
  };
}

FeedbackRequestEmailSetting.propTypes = {
  type: PropTypes.oneOf(['reminder', 'summary', 'alert', 'exclusion']).isRequired,
  organization: PropTypes.shape({
    _id: PropTypes.string,
  }).isRequired,
  isReminder: PropTypes.bool,
  isSummary: PropTypes.bool,
  isAlert: PropTypes.bool,
  isExclusion: PropTypes.bool,
  dirty: PropTypes.bool,
  formDirty: PropTypes.bool,
  fetchData: PropTypes.func.isRequired,
  fetchLogs: PropTypes.func.isRequired,
  updateData: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  sendFrom: PropTypes.string,
  enabled: PropTypes.bool,
  change: PropTypes.func.isRequired,
  sendToOwner: PropTypes.bool,
  searchQuery: PropTypes.shape({}),
  emailSubject: PropTypes.string,
  emailLogo: PropTypes.bool,
  ownerMustBeEnabled: PropTypes.bool,
  // startDate: PropTypes.instanceOf(moment),
  syncLookbackPeriod: PropTypes.number,
  syncLookbackUnit: PropTypes.string,
  syncLookback: PropTypes.string,
  footerHeight: PropTypes.number,
  checkDirty: PropTypes.func.isRequired,
  validCollectors: PropTypes.arrayOf(PropTypes.shape({})),
  sendToDynamic: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  selectedTVCollectorId: PropTypes.string,
  isCollectorRequired: PropTypes.bool,
  automationSource: PropTypes.string,
  opportunityStatus: PropTypes.arrayOf(PropTypes.string),
  meta: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    custom: PropTypes.bool.isRequired,
    fields: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      name: PropTypes.string,
      description: PropTypes.string,
      reference_to: PropTypes.arrayOf(PropTypes.string),
      sync_required: PropTypes.bool,
      is_custom: PropTypes.bool,
    })).isRequired,
  })).isRequired,
  isDeleted: PropTypes.bool,
  sessionId: PropTypes.string,
  weekdays: PropTypes.arrayOf(PropTypes.number),
  hasWeekdaySettings: PropTypes.bool,
  disabled: PropTypes.bool,
};
FeedbackRequestEmailSetting.defaultProps = {
  isSummary: false,
  isReminder: false,
  isExclusion: false,
  enabled: false,
  sendToOwner: false,
  searchQuery: {},
  emailSubject: null,
  sendFrom: null,
  isAlert: false,
  // startDate: null,
  syncLookbackPeriod: 1,
  syncLookbackUnit: 'months',
  syncLookback: '1 months',
  dirty: false,
  formDirty: false,
  emailLogo: false,
  ownerMustBeEnabled: false,
  footerHeight: 0,
  validCollectors: [],
  sendToDynamic: [],
  selectedTVCollectorId: null,
  isCollectorRequired: false,
  opportunityStatus: ['closed'],
  automationSource: 'opportunity',
  isDeleted: false,
  sessionId: null,
  weekdays: [],
  hasWeekdaySettings: false,
};
FeedbackRequestEmailSetting.contextTypes = {
  addNotification: PropTypes.func.isRequired,
};

const schema = Joi.object({
  name: Joi.string().required().messages({
    'string.base': 'Name must be a string',
    'any.required': 'Name is required',
    'string.empty': 'Name is required',
  }),
  days_between_emails: Joi.number().integer().greater(0).required().messages({
    'number.base': 'Days between reminders must be a number',
    'number.greater': 'Days between reminders must be greater than 0',
    'any.required': 'Days between reminders is required',
  }),
  number_of_emails: Joi.number().integer().min(0).required().messages({
    'number.base': 'Number of emails must be a number',
    'number.min': 'Number of emails must be at least 0',
    'any.required': 'Number of emails is required',
  }),
  send_from: Joi.string().email({ tlds: { allow: false } }).allow('', null),
  email_subject: Joi.string().required().messages({
    'string.base': 'Email subject must be a string',
    'any.required': 'Email subject is required',
    'string.empty': 'Email subject is required',
  }),
  email_body: Joi.string().required().messages({
    'string.base': 'Email body must be a string',
    'any.required': 'Email body is required',
    'string.empty': 'Email body is required',
  }),
  email_logo: Joi.boolean().required().messages({
    'boolean.base': 'Email logo must be a boolean',
    'any.required': 'Email logo is required',
  }),
  source: Joi.string().valid('opportunity', 'account').required().messages({
    'any.only': 'Invalid automation type',
    'any.required': 'Automation type is required',
  }),
  opportunity_status: Joi.array().items(Joi.string()).min(1).required().messages({
    'array.base': 'Opportunity status must be an array',
    'array.min': 'Please select at least one "Opportunity Status"',
    'any.required': 'Opportunity status is required',
  }),
});

// Validation function for redux-form
const validate = (data) => {
  const { error } = schema.validate(data, { abortEarly: false });
  if (!error) return {};
  const errors = {};
  error.details.forEach((detail) => {
    const key = detail.path[0];
    errors[key] = detail.message.replace(/"/g, '');
  });
  return errors;
};
FeedbackRequestEmailSetting.contextTypes = {
  addNotification: PropTypes.func.isRequired,
  router: PropTypes.shape({}).isRequired,
};

const FeedbackRequestEmailSettingForm = reduxForm({
  form: 'feedbackRequestEmailSettings',
  validate,
})(FeedbackRequestEmailSetting);

export default connect(mapStateToProps, mapDispatchToProps)(FeedbackRequestEmailSettingForm);
